import { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import { ClipboardList, CheckCircle, AlertCircle, Clock, TrendingUp, TrendingDown } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

function DeliveryDetails() {
  const { API } = useContext(AppContext);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDeliveries();
  }, []);

  const fetchDeliveries = async () => {
    // Mock data - Order vs Actual Delivery
    setDeliveries([
      { id: 'ORD-2025-001', customer: 'ABC Pharma Ltd.', orderQty: 5000, deliveredQty: 5000, orderDate: '2025-02-01', promisedDate: '2025-02-10', actualDate: '2025-02-09', status: 'On Time', variance: 0 },
      { id: 'ORD-2025-002', customer: 'XYZ Industries', orderQty: 3500, deliveredQty: 3500, orderDate: '2025-02-02', promisedDate: '2025-02-12', actualDate: '2025-02-14', status: 'Delayed', variance: 2 },
      { id: 'ORD-2025-003', customer: 'MediCorp India', orderQty: 2000, deliveredQty: 1800, orderDate: '2025-02-03', promisedDate: '2025-02-08', actualDate: '2025-02-08', status: 'Partial', variance: 0 },
      { id: 'ORD-2025-004', customer: 'HealthFirst Inc.', orderQty: 4500, deliveredQty: 4500, orderDate: '2025-02-05', promisedDate: '2025-02-15', actualDate: '2025-02-13', status: 'Early', variance: -2 },
      { id: 'ORD-2025-005', customer: 'Global Metals', orderQty: 1500, deliveredQty: 0, orderDate: '2025-02-06', promisedDate: '2025-02-18', actualDate: '-', status: 'Pending', variance: 0 },
    ]);
    setLoading(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'On Time': return 'bg-green-100 text-green-700 border-green-200';
      case 'Early': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Delayed': return 'bg-red-100 text-red-700 border-red-200';
      case 'Partial': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'Pending': return 'bg-gray-100 text-gray-700 border-gray-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'On Time': return <CheckCircle size={14} />;
      case 'Early': return <TrendingUp size={14} />;
      case 'Delayed': return <AlertCircle size={14} />;
      case 'Partial': return <Clock size={14} />;
      default: return null;
    }
  };

  // Chart data
  const chartData = deliveries.filter(d => d.status !== 'Pending').map(d => ({
    name: d.id.replace('ORD-2025-', ''),
    Ordered: d.orderQty,
    Delivered: d.deliveredQty,
  }));

  const stats = {
    total: deliveries.length,
    onTime: deliveries.filter(d => d.status === 'On Time').length,
    early: deliveries.filter(d => d.status === 'Early').length,
    delayed: deliveries.filter(d => d.status === 'Delayed').length,
    partial: deliveries.filter(d => d.status === 'Partial').length,
  };

  const deliveryRate = ((stats.onTime + stats.early) / (stats.total - deliveries.filter(d => d.status === 'Pending').length) * 100).toFixed(1);

  if (loading) {
    return <div className="text-center py-12">Loading delivery data...</div>;
  }

  return (
    <div className="space-y-6" data-testid="delivery-details-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Delivery Details</h1>
          <p className="text-gray-500 mt-1">Order vs Actual Delivery comparison</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-gradient-to-br from-[#1a237e]/5 to-[#283593]/10 rounded-xl p-5 border border-[#1a237e]/20">
          <p className="text-sm text-[#1a237e] font-medium">Total Orders</p>
          <p className="text-2xl font-bold text-[#1a237e]">{stats.total}</p>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
          <p className="text-sm text-green-600 font-medium">On Time</p>
          <p className="text-2xl font-bold text-green-700">{stats.onTime}</p>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200">
          <p className="text-sm text-blue-600 font-medium">Early</p>
          <p className="text-2xl font-bold text-blue-700">{stats.early}</p>
        </div>
        <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-xl p-5 border border-red-200">
          <p className="text-sm text-red-600 font-medium">Delayed</p>
          <p className="text-2xl font-bold text-red-700">{stats.delayed}</p>
        </div>
        <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-5 border border-orange-200">
          <p className="text-sm text-orange-600 font-medium">Partial</p>
          <p className="text-2xl font-bold text-orange-700">{stats.partial}</p>
        </div>
      </div>

      {/* Chart */}
      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Order vs Delivery Comparison</h3>
            <p className="text-sm text-gray-500">Quantity ordered vs actually delivered</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">On-Time Delivery Rate</p>
            <p className="text-2xl font-bold text-green-600">{deliveryRate}%</p>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb' }} />
            <Legend />
            <Bar dataKey="Ordered" fill="#1a237e" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Delivered" fill="#2e7d32" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Delivery Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-slate-50">
          <div className="flex items-center space-x-2">
            <ClipboardList className="text-[#1a237e]" size={20} />
            <h3 className="text-lg font-bold text-gray-900">Delivery Records</h3>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Order ID</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Customer</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Order Qty</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Delivered Qty</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Promised Date</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Actual Date</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Variance</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {deliveries.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-4 font-mono text-sm text-[#1a237e] font-medium">{item.id}</td>
                  <td className="px-4 py-4 text-sm font-medium text-gray-900">{item.customer}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">{item.orderQty.toLocaleString()} kg</td>
                  <td className="px-4 py-4 text-sm font-semibold text-gray-900">
                    {item.deliveredQty > 0 ? `${item.deliveredQty.toLocaleString()} kg` : '-'}
                    {item.deliveredQty > 0 && item.deliveredQty < item.orderQty && (
                      <span className="text-red-500 text-xs ml-1">({((item.deliveredQty / item.orderQty) * 100).toFixed(0)}%)</span>
                    )}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-600">{item.promisedDate}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">{item.actualDate}</td>
                  <td className="px-4 py-4 text-sm">
                    {item.variance !== 0 && (
                      <span className={`font-medium ${item.variance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {item.variance > 0 ? `+${item.variance}` : item.variance} days
                      </span>
                    )}
                    {item.variance === 0 && item.status !== 'Pending' && <span className="text-gray-400">-</span>}
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(item.status)}`}>
                      {getStatusIcon(item.status)}
                      <span>{item.status}</span>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default DeliveryDetails;
